/**
 * Campaign Composer Page
 * Create and edit email marketing campaigns
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { OutputData } from '@editorjs/editorjs';
import {
  createCampaign,
  updateCampaign,
  getCampaignById,
  sendCampaign,
  sendTestCampaign,
  getAudienceCounts,
  AUDIENCES,
  type CreateCampaignData,
} from '../services/campaigns';
import { EmailEditorJS } from '../components/EditorJS';
import '../styles/pages/campaign-composer.scss';

interface CampaignComposerProps {
  campaignId?: string;
  onNavigate?: (page: 'campaigns') => void;
}

const CampaignComposer: React.FC<CampaignComposerProps> = ({
  campaignId,
  onNavigate,
}) => {
  const isEditing = !!campaignId;

  const [formData, setFormData] = useState<CreateCampaignData>({
    name: '',
    subject: '',
    preview_text: '',
    template_type: 'general_newsletter',
    body_content: '',
    audience_ids: [],
    status: 'draft',
  });

  // Editor.js data state
  const [editorData, setEditorData] = useState<OutputData | undefined>(undefined);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [audienceCounts, setAudienceCounts] = useState<Record<string, number>>({});

  // Load campaign if editing
  useEffect(() => {
    if (isEditing && campaignId) {
      loadCampaign(campaignId);
    }
  }, [campaignId, isEditing]);

  // Load audience counts on mount
  useEffect(() => {
    let isMounted = true;

    const loadCounts = async () => {
      try {
        console.log('[CampaignComposer] Loading audience counts...');
        const counts = await getAudienceCounts();
        console.log('[CampaignComposer] Received counts:', JSON.stringify(counts, null, 2));

        // Only update state if component is still mounted (prevents race condition)
        if (isMounted) {
          setAudienceCounts(counts);
          console.log('[CampaignComposer] State updated with counts');
        }
      } catch (err) {
        console.error('[CampaignComposer] Failed to load audience counts:', err);
      }
    };

    loadCounts();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []);

  const loadCampaign = async (campaignId: string) => {
    try {
      setLoading(true);
      const campaign = await getCampaignById(campaignId);
      if (campaign) {
        setFormData({
          name: campaign.name,
          subject: campaign.subject,
          preview_text: campaign.preview_text || '',
          template_type: campaign.template_type,
          body_content: campaign.body_content,
          audience_ids: campaign.audience_ids,
          status: campaign.status === 'draft' || campaign.status === 'scheduled' ? campaign.status : 'draft',
        });
        // Note: Editor.js data is not persisted - only the HTML output is stored
        // When editing, the editor starts fresh and the user rebuilds content
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  // Handle editor content changes
  const handleEditorChange = useCallback((data: OutputData, html: string) => {
    setEditorData(data);
    setFormData((prev) => ({ ...prev, body_content: html }));
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAudienceToggle = (audienceId: string) => {
    setFormData((prev) => ({
      ...prev,
      audience_ids: prev.audience_ids.includes(audienceId)
        ? prev.audience_ids.filter((id) => id !== audienceId)
        : [...prev.audience_ids, audienceId],
    }));
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      setError(null);

      if (isEditing && campaignId) {
        await updateCampaign(campaignId, { ...formData, status: 'draft' });
        setSuccess('Campaign saved as draft');
      } else {
        await createCampaign({ ...formData, status: 'draft' });
        setSuccess('Campaign created');
        setTimeout(() => onNavigate?.('campaigns'), 1000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if (!window.confirm('Are you sure you want to send this campaign now?')) {
      return;
    }

    try {
      setSending(true);
      setError(null);

      let currentCampaignId = campaignId;

      // If not saved yet, create it first
      if (!isEditing) {
        const campaign = await createCampaign(formData);
        currentCampaignId = campaign.id;
      } else {
        // Update campaign before sending
        await updateCampaign(currentCampaignId!, formData);
      }

      // Send campaign
      await sendCampaign(currentCampaignId!);
      setSuccess('Campaign sent successfully!');

      // Navigate to campaigns list after a delay
      setTimeout(() => onNavigate?.('campaigns'), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    try {
      setSendingTest(true);
      setError(null);

      let currentCampaignId = campaignId;

      // If not saved yet, save it first as a draft
      if (!isEditing) {
        const campaign = await createCampaign({ ...formData, status: 'draft' });
        currentCampaignId = campaign.id;
        setSuccess('Campaign saved as draft');
      } else if (campaignId) {
        // Update existing draft before sending test
        await updateCampaign(campaignId, formData);
      }

      // Send test email
      await sendTestCampaign(currentCampaignId!, 'carljbusch@gmail.com');
      setSuccess('Test email sent to carljbusch@gmail.com! Check your inbox.');
    } catch (err: any) {
      setError(err.message || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="campaign-composer">
        <div className="campaign-composer__loading">
          <p>Loading campaign...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-composer">
      <div className="campaign-composer__header">
        <div>
          <h1>{isEditing ? 'Edit Campaign' : 'Create New Campaign'}</h1>
          <p className="campaign-composer__subtitle">
            Compose and send email campaigns to your audiences
          </p>
        </div>
        <button
          className="btn btn--outline"
          onClick={() => onNavigate?.('campaigns')}
        >
          Back to Campaigns
        </button>
      </div>

      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert--success" role="alert">
          {success}
        </div>
      )}

      <div className="campaign-composer__content">
        <div className="campaign-composer__form">
          <form>
            {/* Campaign Settings */}
            <div className="card campaign-composer__section">
              <h2>Campaign Settings</h2>

              <div className="form__group">
                <label htmlFor="name" className="form__label">
                  Campaign Name <span className="form__required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form__input"
                  placeholder="e.g., January Wine Sale"
                />
                <small className="form__help">
                  Internal name for this campaign
                </small>
              </div>

              <div className="form__group">
                <label htmlFor="template_type" className="form__label">
                  Template Type <span className="form__required">*</span>
                </label>
                <select
                  id="template_type"
                  name="template_type"
                  value={formData.template_type}
                  onChange={handleChange}
                  required
                  className="form__select"
                >
                  {Object.entries(AUDIENCES).map(([key, audience]) => (
                    <option key={key} value={key}>
                      {audience.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Email Content */}
            <div className="card campaign-composer__section">
              <h2>Email Content</h2>

              <div className="form__group">
                <label htmlFor="subject" className="form__label">
                  Subject Line <span className="form__required">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="form__input"
                  placeholder="e.g., Hi {{firstName}}, Save 20% on All Red Wines This Weekend"
                />
                <small className="form__help">
                  Use {'{{'} firstName {'}}'} or {'{{'} lastName {'}}'} for personalization
                </small>
              </div>

              <div className="form__group">
                <label htmlFor="preview_text" className="form__label">
                  Preview Text
                </label>
                <input
                  type="text"
                  id="preview_text"
                  name="preview_text"
                  value={formData.preview_text}
                  onChange={handleChange}
                  className="form__input"
                  placeholder="Your exclusive offer, {{firstName}}!"
                />
                <small className="form__help">
                  Short preview text shown in email inbox. Supports {'{{'} firstName {'}}'} and {'{{'} lastName {'}}'}
                </small>
              </div>

              <div className="form__group">
                <label className="form__label">
                  Email Content <span className="form__required">*</span>
                </label>
                <small className="form__help" style={{ display: 'block', marginBottom: '12px' }}>
                  Click the + button or press Tab to add blocks. Use {'{{firstName}}'}, {'{{lastName}}'}, or {'{{email}}'} for personalization.
                </small>
                <EmailEditorJS
                  initialData={editorData}
                  onChange={handleEditorChange}
                  placeholder="Start composing your email..."
                />
              </div>
            </div>

            {/* Audience Selection */}
            <div className="card campaign-composer__section">
              <h2>Target Audiences</h2>
              <p className="campaign-composer__help">
                Select one or more audiences to send this campaign to.
              </p>

              <div className="campaign-composer__audiences">
                {Object.entries(AUDIENCES).map(([key, audience]) => {
                  const count = audienceCounts[key];
                  const displayName = count !== undefined
                    ? `${audience.name} (${count} subscriber${count !== 1 ? 's' : ''})`
                    : audience.name;

                  return (
                    <label key={key} className="campaign-composer__audience">
                      <input
                        type="checkbox"
                        checked={formData.audience_ids.includes(key)}
                        onChange={() => handleAudienceToggle(key)}
                      />
                      <div>
                        <strong>{displayName}</strong>
                        <p>{audience.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>

              {formData.audience_ids.length === 0 && (
                <p className="campaign-composer__warning">
                  ⚠️ Please select at least one audience
                </p>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Actions */}
      <div className="campaign-composer__actions">
        <div className="campaign-composer__actions-left">
          <button
            type="button"
            className="btn btn--outline"
            onClick={handleSaveDraft}
            disabled={saving || sending || sendingTest}
          >
            {saving ? 'Saving...' : 'Save as Draft'}
          </button>

          <button
            type="button"
            className="btn btn--secondary"
            onClick={handleSendTest}
            disabled={
              saving ||
              sending ||
              sendingTest ||
              !formData.subject ||
              !formData.body_content
            }
            title="Send a test preview to carljbusch@gmail.com"
          >
            {sendingTest ? 'Sending Test...' : '📧 Send Test Email'}
          </button>
        </div>

        <button
          type="button"
          className="btn btn--primary"
          onClick={handleSendNow}
          disabled={
            saving ||
            sending ||
            sendingTest ||
            !formData.name ||
            !formData.subject ||
            !formData.body_content ||
            formData.audience_ids.length === 0
          }
        >
          {sending ? 'Sending...' : 'Send Now'}
        </button>
      </div>
    </div>
  );
};

export default CampaignComposer;
